/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react'

import globalNotification from 'live/services/global-notification'
import i18n from 'live/services/i18n'

import { CreateEditAssetTypeModal } from '../../../../../AssetTypeConfiguration/components/AssetTypeList/components'
import { RemoveConfirmationModal } from '../../../../../generic'
import {
    AssetType,
    createAssetType,
    getAssetTypes,
    getAssetTypesToResourceGroup
} from '../../../../../utils/services/assetType'
import {
    createResourceGroup,
    deleteResourceGroup,
    getResourceGroup,
    ResourceGroup,
    updateResourceGroup
} from '../../../../../utils/services/resourceGroup'
import { listUser } from '../../../../../utils/services/users'
import { FormContainer, FormField, NewGroupScreenContainer, SaveBar, SelectEdit, TopBar } from './styles'

interface IProps {
    mode: string
    setMode: (k: string) => void
    idToEdit?: number
}

export const NewGroupScreen: React.FC<IProps> = ({ mode, setMode, idToEdit }) => {
    const [loading, setLoading] = useState<boolean>()
    const [resourceTypes, setResourceTypes] = useState<AssetType[]>()
    const [users, setUsers] = useState<LiveUser[]>()
    const [showCreateModal, setShowCreateModal] = useState<boolean>(false)
    const [dataToEdit, setDataToEdit] = useState<ResourceGroup>()

    const [name, setName] = useState<string>()
    const [selectedUsers, setSelectedUsers] = useState<number[]>([])
    const [selectedTypes, setSelectedTypes] = useState<AssetType[]>([])

    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)

    const backToList = (): void => {
        setMode('list')
    }

    const onCancel = (): void => {
        setMode('list')
    }

    const validateFields = (): boolean => {
        if (name && selectedTypes.length > 0 && selectedUsers.length > 0) {
            return false
        }

        return true
    }

    const selectAssetTypes = resourceTypes?.map(list => {
        return { label: list.name, value: list }
    })

    const selectLiveUsers = users?.map(list => {
        return { label: list.name, value: list.id }
    })

    const handleGetResourceGroupToEdit = async (): Promise<void> => {
        try {
            const data = await getResourceGroup(idToEdit)
            setDataToEdit(data)
        } catch (error) {
            globalNotification.error(error.message ?? i18n('Unable to fetch resource group data.'))
        } finally {
            setLoading(false)
        }
    }

    const handlePostOrEditResourceGroup = async (): Promise<void> => {
        if (mode === 'create') {
            try {
                const dataToRequisition = {
                    assetTypes: selectedTypes,
                    name: name,
                    liveUserIds: selectedUsers,
                    id: null
                }
                await createResourceGroup(dataToRequisition)
                globalNotification.success(i18n('Resource group created succesfully.'))
            } catch (error) {
                globalNotification.error(error.message ?? i18n('Unable to create resource group.'))
            } finally {
                setLoading(false)
                setMode('list')
            }
        }
        if (mode === 'edit') {
            try {
                await updateResourceGroup({
                    assetTypes: selectedTypes,
                    name: name,
                    liveUserIds: selectedUsers,
                    id: dataToEdit.id
                })
                globalNotification.success(i18n('Resource group updated succesfully.'))
            } catch (error) {
                globalNotification.error(error.message ?? i18n('Unable to update resource group.'))
            } finally {
                setLoading(false)
                setMode('list')
            }
        }
    }

    const handleGetResourceTypes = async (): Promise<void> => {
        try {
            setLoading(true)
            const data = await getAssetTypesToResourceGroup()
            setResourceTypes(data)
        } catch (error) {
            globalNotification.error(error.message ?? i18n('Unable to fetch resource types.'))
        } finally {
            setLoading(false)
        }
    }

    const handleGetLiveUsers = async (): Promise<void> => {
        try {
            setLoading(true)
            const data = await listUser()
            setUsers(data)
        } catch (error) {
            globalNotification.error(error.message ?? i18n('Unable to fetch live users.'))
        } finally {
            setLoading(false)
        }
    }

    const handleCreateNewAssetType = async (assetTypeName: string): Promise<void> => {
        try {
            setLoading(true)
            const newAssetType: AssetType = { id: assetTypeName.replace(' ', '').substring(0, 8), name: assetTypeName }
            await createAssetType(newAssetType)
            globalNotification.success(i18n('Resource type created successfully.'))
            handleGetResourceTypes()
        } catch (error) {
            globalNotification.error(i18n('Unable to create resource type.'))
            setLoading(false)
        }
    }

    const handleAddUser = (id: number): void => {
        setSelectedUsers(users => [...users, id])
    }

    const handleAddType = (type: AssetType): void => {
        setSelectedTypes(types => [...types, type])
    }

    const handleDeleteUser = (id: number): void => {
        const index = selectedUsers.indexOf(id)
        setSelectedUsers([...selectedUsers.slice(0, index), ...selectedUsers.slice(index + 1)])
    }

    const handleDeleteType = (type: AssetType): void => {
        const index = selectedTypes.indexOf(type)
        setSelectedTypes([...selectedTypes.slice(0, index), ...selectedTypes.slice(index + 1)])
    }

    const handleCloseProtocol = (): void => {
        setShowCreateModal(false)
    }

    const showModal = (): void => {
        setShowCreateModal(true)
    }

    useEffect(() => {
        handleGetResourceTypes()
        handleGetLiveUsers()
        handleGetResourceGroupToEdit()
    }, [])

    useEffect(() => {
        if (dataToEdit) {
            setName(dataToEdit.name)
            setSelectedTypes(dataToEdit?.assetTypes)
            setSelectedUsers(dataToEdit?.liveUserIds)
        }
    }, [dataToEdit])

    return (
        <>
            <NewGroupScreenContainer>
                <div className="top-bar">
                    <TopBar className="topbarcontainer">
                        <div>
                            <button className="back-button" id="back-button" onClick={backToList}>
                                <svg
                                    width="18"
                                    height="14"
                                    viewBox="0 0 18 14"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M0 7L7.58333 0.5L7.58333 4.83333H17.3333L17.3333 12.4167H14.0833V9.16667H7.58333L7.58333 13.5L0 7Z"
                                        fill="#37A3EF"
                                    />
                                </svg>
                            </button>
                            <span>{i18n('Groups')}</span>
                        </div>
                        {dataToEdit && (
                            <div className="delete">
                                <button id="delete-resource-group" onClick={() => setShowDeleteModal(true)}>
                                    {i18n('Delete')}
                                    <i className="far fa-trash-alt"></i>
                                </button>
                            </div>
                        )}
                    </TopBar>
                </div>
                <FormContainer className="formcontainer">
                    {!loading ? (
                        <>
                            <div className="collum">
                                <FormField>
                                    <label htmlFor="name" className="label">
                                        {i18n('Group name')}
                                    </label>
                                    <input
                                        type="text"
                                        className="group-name"
                                        id="group-name"
                                        value={name}
                                        onChange={e => setName(e.target.value)}
                                    />
                                </FormField>
                                <FormField>
                                    <label htmlFor="user" className="label">
                                        {i18n('Users')}
                                    </label>
                                    <SelectEdit
                                        options={selectLiveUsers?.filter(el => !selectedUsers?.includes(el.value))}
                                        isSearchable={true}
                                        onChange={value => {
                                            handleAddUser(value)
                                        }}
                                        searchable={true}
                                        clearable={false}
                                        id="group-users"
                                    />
                                    <div className="selected-asset-types">
                                        {selectedUsers?.map(user => {
                                            return (
                                                <div key={user} className="asset-types-fields">
                                                    <span>
                                                        {users?.filter(el => el.id === user).map(name => name.name)}
                                                    </span>
                                                    <button className="close" onClick={() => handleDeleteUser(user)}>
                                                        <i className="fal fa-times"></i>
                                                    </button>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </FormField>
                            </div>
                            <div className="collum types">
                                <FormField>
                                    <label htmlFor="resourcetype" className="label">
                                        {i18n('Resource types')}
                                    </label>
                                    <SelectEdit
                                        options={selectAssetTypes?.filter(el => !selectedTypes?.includes(el.value))}
                                        isSearchable={true}
                                        onChange={value => {
                                            handleAddType(value)
                                        }}
                                        searchable={true}
                                        clearable={false}
                                        id="group-types"
                                    />
                                    <div className="selected-users">
                                        {selectedTypes?.map(type => {
                                            return (
                                                <div key={type.id} className="users-fields">
                                                    <span>{type.name}</span>
                                                    <button className="close" onClick={() => handleDeleteType(type)}>
                                                        <i className="fal fa-times"></i>
                                                    </button>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </FormField>
                                <button onClick={showModal}>
                                    <i className="fas fa-plus"></i> {i18n('Create a new resource type')}
                                </button>
                            </div>
                        </>
                    ) : (
                        <div className="loading-spin">
                            <i className="fa fa-spinner fa-spin fa-x2" />
                        </div>
                    )}
                </FormContainer>
                <SaveBar className="container">
                    <button
                        disabled={validateFields()}
                        className="save-button"
                        id="save-button"
                        onClick={handlePostOrEditResourceGroup}
                    >
                        {i18n('Save')}
                    </button>
                    <button className="cancel-button" id="cancel-button" onClick={onCancel}>
                        {i18n('Cancel')}
                    </button>
                </SaveBar>
            </NewGroupScreenContainer>
            <RemoveConfirmationModal
                setShow={setShowDeleteModal}
                show={showDeleteModal}
                handleClose={handleCloseProtocol}
                type={i18n('resource group')}
                message={i18n('If you really want to delete the resource group, type')}
                confirmationMessage={i18n('Resource group deleted succesfully.')}
                removalMessage={`${i18n('Are you sure you want remove the resource group')} ${dataToEdit?.name}?`}
                onDelete={async () => {
                    await deleteResourceGroup(dataToEdit.id)
                    setMode('list')
                }}
            />
            <CreateEditAssetTypeModal
                show={showCreateModal}
                setShow={setShowCreateModal}
                type={'create'}
                handleClose={handleCloseProtocol}
                handleCreate={handleCreateNewAssetType}
            />
        </>
    )
}
